import React, { useContext, useState, useEffect, useMemo, useRef } from "react";
import IndentDataService from "../services/IndentService";
import { useTable, useSortBy } from "react-table";
import { useNavigate } from "react-router-dom";
import { ApplicationContext } from "./ApplicationContext";
import { formatDate } from "./utils";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import log from "../services/logger";

const IndentsList = () => {
  const [indents, setIndents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useState({
    code: "",
    customerName: "",
    dateFrom: "",
    dateTo: "",
  });
  const indentsRef = useRef(indents);
  const navigate = useNavigate();
  const { userRole } = useContext(ApplicationContext);
  const [displayedIndents, setDisplayedIndents] = useState([]);

  useEffect(() => {
    retrieveIndents();
  }, []);

  const retrieveIndents = async () => {
    setLoading(true);
    try {
      const response = await IndentDataService.getAll();
      setIndents(response.data);
      setDisplayedIndents(response.data);
      indentsRef.current = response.data;
    } catch (error) {
      toast.error("Greška pri učitavanju porudžbina");
      log.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchParamChange = (key, value) => {
    setSearchParams(prev => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setSearchParams({
      code: "",
      customerName: "",
      dateFrom: "",
      dateTo: "",
    });
    setDisplayedIndents(indentsRef.current);
  };

  const applyFilters = () => {
    const filteredIndents = indentsRef.current.filter(indent => {
      const indentDate = new Date(indent.creationTime);
      const matchesCode = !searchParams.code || 
        indent.code.toLowerCase().includes(searchParams.code.toLowerCase());
      const matchesCustomer = !searchParams.customerName || 
        indent.customerNameOfTheLegalEntity.toLowerCase().includes(searchParams.customerName.toLowerCase());
      const matchesDateFrom = !searchParams.dateFrom || 
        indentDate >= new Date(searchParams.dateFrom);
      const matchesDateTo = !searchParams.dateTo || 
        indentDate <= new Date(searchParams.dateTo);

      return matchesCode && matchesCustomer && matchesDateFrom && matchesDateTo;
    });

    setDisplayedIndents(filteredIndents);
  };

  const handleAction = async (action, indentCode) => {
    setLoading(true);
    try {
      switch (action) {
        case 'delete':
          await IndentDataService.remove(indentCode);
          toast.success(`Uspešno obrisana porudžbina [${indentCode}]`);
          break;
        case 'deliver':
          await IndentDataService.confirmIndentDelivery(indentCode);
          toast.success(`Uspešno isporučena porudžbina [${indentCode}]`);
          break;
        default:
          break;
      }
      await retrieveIndents();
    } catch (error) {
      toast.error(`Greška pri izvršavanju akcije: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleShowActiveIndents = () => {
    setLoading(true);
    IndentDataService.getAllActiveIndents()
      .then((response) => {
        setIndents(response.data);
        setDisplayedIndents(response.data);
        indentsRef.current = response.data;
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
        toast.error("Greška pri učitavanju aktivnih porudžbina");
        log.error(e);
      });
  };

  const handleShowInactiveIndents = () => {
    setLoading(true);
    IndentDataService.getAllInactiveIndents()
      .then((response) => {
        setIndents(response.data);
        setDisplayedIndents(response.data);
        indentsRef.current = response.data;
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
        toast.error("Greška pri učitavanju neaktivnih porudžbina");
        log.error(e);
      });
  };

  const handleShowAllIndents = () => {
    setLoading(true);
    IndentDataService.getAllIndents()
      .then((response) => {
        setIndents(response.data);
        setDisplayedIndents(response.data);
        indentsRef.current = response.data;
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
        toast.error("Greška pri učitavanju neaktivnih porudžbina");
        log.error(e);
      });
  };

  const columns = useMemo(() => [
    {
      Header: "Šifra Klijenta",
      accessor: "customerCode",
    },
    {
      Header: "Klijent",
      accessor: "customerNameOfTheLegalEntity",
    },
    {
      Header: "Adresa Dostave",
      accessor: row => `${row.deliveryAddressCity}, ${row.deliveryAddressLocation}`,
    },
    {
      Header: "ID Porudžbine",
      accessor: "code",
    },
    {
      Header: "Status",
      accessor: "indentStatus",
      Cell: ({ value }) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusStyle(value)}`}>
          {value}
        </span>
      ),
    },
    {
      Header: "Datum",
      accessor: "creationTime",
      Cell: ({ value }) => formatDate(value),
    },
    {
      Header: "Akcije",
      accessor: "actions",
      Cell: ({ row }) => (
        <div className="flex space-x-2">
          <button
            onClick={() => navigate(`/indents/entries/${row.original.code}`)}
            className="px-3 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200"
          >
            <i className="far fa-eye mr-1"></i> Pregled
          </button>
          
          {row.original.indentStatus === "PENDING" && (
            <>
              <button
                onClick={() => handleAction('delete', row.original.code)}
                className="px-3 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200"
              >
                <i className="fas fa-trash mr-1"></i> Izbriši
              </button>
              
              {(userRole === "ROLE_MAGACIONER" || userRole === "ROLE_ADMIN" || userRole === "ROLE_FAKTURISTA") && (
                <button
                  onClick={() => handleAction('deliver', row.original.code)}
                  className="px-3 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200"
                >
                  <i className="fas fa-check mr-1"></i> Potvrdi
                </button>
              )}
            </>
          )}
        </div>
      ),
    },
  ], [navigate, userRole]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data: displayedIndents,
      initialState: {
        sortBy: [{ id: "creationTime", desc: true }],
      },
    },
    useSortBy
  );

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Search Filters */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {userRole !== "ROLE_USER" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Klijent
              </label>
              <input
                type="text"
                value={searchParams.customerName}
                onChange={(e) => handleSearchParamChange('customerName', e.target.value)}
                placeholder="Ime klijenta"
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ID Porudžbine
            </label>
            <input
              type="text"
              value={searchParams.code}
              onChange={(e) => handleSearchParamChange('code', e.target.value)}
              placeholder="ID porudžbine"
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Od datuma
            </label>
            <input
              type="date"
              value={searchParams.dateFrom}
              onChange={(e) => handleSearchParamChange('dateFrom', e.target.value)}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Do datuma
            </label>
            <input
              type="date"
              value={searchParams.dateTo}
              onChange={(e) => handleSearchParamChange('dateTo', e.target.value)}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        
        <div className="flex flex-wrap justify-between mt-6">
          <div className="flex flex-wrap gap-2 mb-2">
            {userRole !== "ROLE_USER" && (
              <>
                <button
                  onClick={handleShowActiveIndents}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200"
                >
                  Aktivne Porudžbine
                </button>
                <button
                  onClick={handleShowInactiveIndents}
                  className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors duration-200"
                >
                  Neaktivne Porudžbine
                </button>
                <button
                  onClick={handleShowAllIndents}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200"
                >
                  Sve Porudžbine
                </button>
              </>
            )}
          </div>
          
          <div className="flex justify-end space-x-4">
            <button
              onClick={resetFilters}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors duration-200"
            >
              Resetuj
            </button>
            <button
              onClick={applyFilters}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200"
            >
              Primeni filtere
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table {...getTableProps()} className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                {headerGroups.map(headerGroup => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map(column => (
                      <th
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {column.render('Header')}
                        <span className="ml-2">
                          {column.isSorted ? (column.isSortedDesc ? '↓' : '↑') : ''}
                        </span>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()} className="bg-white divide-y divide-gray-200">
                {rows.map(row => {
                  prepareRow(row);
                  return (
                    <tr
                      {...row.getRowProps()}
                      className={`hover:bg-gray-50 transition-colors duration-150 ${getRowStyle(row.original.indentStatus)}`}
                    >
                      {row.cells.map(cell => (
                        <td {...cell.getCellProps()} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {cell.render('Cell')}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Utility functions for styling
const getStatusStyle = (status) => {
  switch (status) {
    case 'PENDING':
      return 'bg-yellow-100 text-yellow-800';
    case 'DELIVERED':
      return 'bg-green-100 text-green-800';
    case 'CANCELED':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getRowStyle = (status) => {
  switch (status) {
    case 'PENDING':
      return 'bg-yellow-50';
    case 'DELIVERED':
      return 'bg-green-50';
    case 'CANCELED':
      return 'bg-red-50';
    default:
      return '';
  }
};

export default IndentsList;
